import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const StyledInput = styled.div`
    position: relative;
    margin-right: 16px;
    margin-bottom: 28px;
    @media screen and (max-width: 710px) {
        margin-right: 0;
    }
    @media screen and (min-width: 720px) and (max-width: 1024px) {
        margin-right: 0;
    }
    flex-basis: 0;
    flex-grow: 1;
    label {
        position: absolute;
        top: 16px;
        left: 16px;
        ${props => (props.hasError ? 'color: #cf0a2c;' : 'color: #757575;')}
    }

    input {
        height: 56px;
        width: 100%;
        border: 1px solid #b5b5b5;
        transition: border 0.2s;
        box-sizing: border-box;
        padding: 11px 0 0 15px;
        color: #404041;
        font-family: ${props => props.theme.fonts[0]}, sans-serif;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 24px;
    }
    input:focus {
        border: ${props =>
            props.hasError
                ? `1px solid ${props => props.theme.colors.error}`
                : `2px solid ${props.theme.colors.primaryYellow} !important`};
        outline: none;
    }
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    input[type='number'] {
        -moz-appearance: textfield;
    }

    .required-indicator {
        position: absolute;
        right: 0;
        top: 0;
        width: 6px;
        height: 56px;
        background-color: ${props =>
            props.hasError ? '#cf0a2c' : props.theme.colors.primaryYellow};
        transition: background-color 0.2s;
    }

    div {
        font-size: 12px;
        color: #cf0a2c;
        line-height: 14px;
        position: absolute;
        padding-top: 5px;
        ${props =>
            props.name === 'mailingEmail' || props.name === 'additionalEmail'
                ? 'margin-top: 7px;'
                : 'margin:auto;'}
    }
`;

const variants = {
    hasError: { border: '1px solid #CF0A2C', opacity: 1 },
    valid: { border: '1px solid #b5b5b5', opacity: 1 },
    initial: { border: '1px solid #b5b5b5', opacity: 0 },
};

const labelVariants = {
    selected: {
        top: 3,
        opacity: 1,
        x: 0,
        fontSize: 12,
    },
    blur: {
        top: 16,
        opacity: 1,
        x: 0,
        fontSize: 16,
    },
};

const ErrorVariants = {
    show: { opacity: 1 },
    hide: { opacity: 0 },
};

function HookFormInput({
    hasError,
    errorText,
    label,
    register,
    name,
    required,
    min,
    max,
    minLength,
    maxLength,
    pattern,
    validate,
    value,
    type,
    placeholder,
    onChange,
    maskType,
    defaultValue,
    onKeyDown,
}) {
    //These are used to drive the various Framer Motion animation states for the input
    const [selected, setSelected] = useState(false);
    const [hasValue, setHasValue] = useState(value ? true : false);
    const oldValueRef = useRef(0);

    if ((value || defaultValue) && !selected) {
        setSelected(true);
    }

    var ValidationRules = {};
    if (required) {
        ValidationRules.required = {
            value: required,
            message: `${label} is required`,
        };
    }
    if (min) {
        ValidationRules.min = {
            value: min,
            message: `${label} must be more than ${min}`,
        };
    }
    if (max) {
        ValidationRules.max = {
            value: max,
            message: `${label} must be less than ${max}`,
        };
    }
    if (minLength) {
        ValidationRules.minLength = {
            value: minLength,
            message: `${label} must be at least ${minLength} characters`,
        };
    }
    if (maxLength) {
        ValidationRules.maxLength = {
            value: maxLength,
            message: `${label} must be fewer than ${maxLength} characters`,
        };
    }
    if (pattern) {
        ValidationRules.pattern = {
            value: pattern,
            message: `${label} is not in the right format`,
        };
    }
    //This options takes a callback function. see https://react-hook-form.com/api#register
    if (validate) {
        ValidationRules.validate = validate;
    }

    return (
        <StyledInput
            hasError={hasError}
            required={required}
            hasValue={hasValue}
            name={name}
        >
            <motion.input
                name={name}
                ref={register(ValidationRules)}
                type={type}
                value={value}
                animate={hasError ? 'hasError' : 'valid'}
                initial={'initial'}
                variants={variants}
                onFocus={() => setSelected(true)}
                onBlur={e => {
                    setSelected(false);
                    setHasValue(e.target.value ? true : false);
                }}
                onLoad={e => {
                    if (value || e.target.value) {
                        setSelected(true);
                    }
                }}
                onChange={e => {
                    setHasValue(e.target.value ? true : false);
                    onChange && onChange(e);
                    maskInput(e, maskType);
                }}
                maxlength={maxLength}
                minlength={minLength}
                min={min}
                max={max}
                id={name}
                placeholder={selected ? placeholder : ''}
                defaultValue={defaultValue}
            />
            <motion.label
                animate={
                    selected || hasValue || type === 'date'
                        ? 'selected'
                        : 'blur'
                }
                initial={{ opacity: 0 }}
                variants={labelVariants}
                htmlFor={name}
            >
                {label}
            </motion.label>
            <motion.div
                initial={{ opacity: 0 }}
                variants={ErrorVariants}
                animate={hasError ? 'show' : 'hide'}
            >
                {errorText}
            </motion.div>

            {required && <span className="required-indicator"></span>}
        </StyledInput>
    );

    function maskInput(e, maskType) {
        if (maskType === 'Phone' || maskType === 'Fax') {
            var x = e.target.value
                        .replace(/\D/g, "")
                        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
                let caret1 = e.target.selectionStart;
                e.target.value = !x[2]
                ? x[1]
                : x[1] + "-" + x[2] + (x[3] ? "-" + x[3] : "");
                const perviousCount = oldValueRef.current.length;
                if (
                    (caret1 === 4 && perviousCount < e.target.value.length) ||
                    (caret1 === 8 && perviousCount < e.target.value.length)
                  ) {
                    caret1 = caret1 + 1;
                  }
                oldValueRef.current = e.target.value;
                e.target.setSelectionRange(caret1, caret1);
        } else if (maskType === 'Number') {
            e.target.value = e.target.value
                .replace(/[^0-9.]/g, '')
                .replace(/(.{4})\./g, '$1');
        } else if (maskType === 'FederalId') {
            var y = e.target.value
                .replace(/\D/g, '')
                .match(/(\d{0,2})(\d{0,7})/);
            let caret1 = e.target.selectionStart+1;
            e.target.value = !y[2] ? y[1] : +y[1] + '-' + y[2];
            const perviousCount = oldValueRef.current.length;
            if (
                (caret1 === 4 && perviousCount < e.target.value.length) ||
                (caret1 === 8 && perviousCount < e.target.value.length)
              ) {
                caret1 = caret1 + 1;
              }
            oldValueRef.current = e.target.value;
            e.target.setSelectionRange(caret1, caret1-1);
        }
    }
}

HookFormInput.propTypes = {
    hasError: PropTypes.bool,
    errorText: PropTypes.string,
    label: PropTypes.string.isRequired,
    register: PropTypes.func,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    pattern: PropTypes.instanceOf(RegExp),
    validate: PropTypes.func,
    // value: PropTypes.string,
    type: PropTypes.string,
    placeholder: PropTypes.string,
};

HookFormInput.defaultProps = {
    label: 'Pass me a label',
    name: 'input',
};

export default HookFormInput;
